import { Translation } from '../Translations';

/**
 * Content model behind the Info section (#4894) — an in-app guide to how the finance flows are actually
 * run, written for the officer doing the work rather than for a developer.
 *
 * <p>Everything here is data. The components render loops over these types and hold no prose of their own,
 * so correcting a step is editing a string in {@link INFO_PAGES}, not touching a template. Every visible
 * string is a {@link Translation} so the section follows the app's language toggle.</p>
 *
 * <p>Nothing is fetched. The guide describes the product rather than any tenant's data, so it ships with
 * the bundle; if it ever needs to be editable, these types are already the shape an API would return.</p>
 */

/** A link out of the guide — either to a route in this app or to another module. */
export interface InfoLink {
  label: Translation;
  /** Route inside Finance.UI, e.g. '/ap-documents'. */
  route?: string;
  /** Absolute URL, opened in a new tab. Used for the other modules' UIs. */
  externalUrl?: string;
  icon?: string;
}

/** A `term — description` line in a step. */
export interface InfoBullet {
  term: Translation;
  description: Translation;
}

/** A rule that only applies in some circumstance: "If X — then Y". */
export interface InfoCondition {
  when: Translation;
  then: Translation;
  /** Optional pointer to where the reader would go to act on it. */
  link?: InfoLink;
}

/**
 * A step's content for ONE branch, replacing the step's shared content.
 *
 * <p>The presence of a variant is what makes a step "differ" for that branch — see
 * {@link InfoStep.variants}. There is no separate "differs" flag to keep in step with the content, because
 * a flag and the content it describes drift apart the first time someone edits one and not the other.</p>
 */
export interface InfoStepVariant {
  body?: Translation;
  bullets?: InfoBullet[];
  conditions?: InfoCondition[];
  links?: InfoLink[];
  /**
   * This branch genuinely differs here, but the text has not been written yet. The step renders a
   * "content pending" note instead of prose, and still counts as differing in the comparison matrix —
   * an unwritten difference is a difference, and hiding it would misdescribe the flow.
   */
  pending?: boolean;
}

/** One numbered step of a flow. */
export interface InfoStep {
  id: string;
  title: Translation;
  /** Shared content — used by every branch that has no variant. */
  body?: Translation;
  bullets?: InfoBullet[];
  conditions?: InfoCondition[];
  links?: InfoLink[];
  /**
   * Branch-specific content, keyed by {@link InfoBranch.id}. A branch listed here differs at this step;
   * a branch absent from it follows the shared content above.
   */
  variants?: Record<string, InfoStepVariant>;
}

/** One way of starting a flow. The choice decides what the flow produces. */
export interface InfoBranch {
  id: string;
  label: Translation;
  /** The one-line summary under the tab, e.g. 'Raw-material PO → creates Invoice'. */
  caption: Translation;
  /** Longer description, shown in the side rail while this branch is selected. */
  description: Translation;
  /** Who runs it, e.g. 'AP Clerk — posting by AP Supervisor'. */
  role?: Translation;
  /** Where the reader goes to do it for real. */
  link?: InfoLink;
}

/** Which sidebar group a page belongs to. */
export type InfoGroup = 'flows' | 'business';

/**
 * One page of the guide. A page either walks through a branching flow (branches + steps) or simply
 * explains a concept (body) — the Tax Authorization page is the latter.
 */
export interface InfoPage {
  slug: string;
  group: InfoGroup;
  /** Label in the sidebar. */
  navLabel: Translation;
  /**
   * Slug of the page this one sits under in the sidebar. The parent renders as a heading rather than a
   * link when it is not itself a page — Provider Details is a screen in the app, not an article here.
   */
  parentSlug?: string;
  title: Translation;
  subtitle: Translation;
  /** Trail shown above the title. The page's own title is appended by the component. */
  breadcrumb: Translation[];
  /** Recorded walkthrough. The button only renders for pages that have one. */
  scribeUrl?: string;
  /** Flow pages: the ways of starting, and the steps that follow. */
  branches?: InfoBranch[];
  steps?: InfoStep[];
  /** Concept pages: the explanation. */
  body?: Translation;
  /** Where to go to act on what the page describes. */
  links?: InfoLink[];
}

/** A sidebar group with its pages, in display order. */
export interface InfoGroupDefinition {
  id: InfoGroup;
  label: Translation;
}

/**
 * Whether a step differs for a branch. Single source for both the step's badge and the comparison
 * matrix, so the two can never disagree.
 */
export function stepDiffersForBranch(step: InfoStep, branchId: string): boolean {
  return !!step.variants?.[branchId];
}

/** The content a step shows for a branch: its variant if it has one, otherwise the shared content. */
export function stepContentForBranch(step: InfoStep, branchId: string): InfoStepVariant {
  const variant = step.variants?.[branchId];
  if (variant) {
    return variant;
  }
  return { body: step.body, bullets: step.bullets, conditions: step.conditions, links: step.links };
}
